/**
 * Base URL for the Cemex API, configured via environment variable, NEXT_ISSERVER da true en el server y undefined en client, esto nos permite que nunca falle a donde tiene que ir el endpoint.
 */
export const alertAIApiUrl: string | undefined = process.env.NEXT_ISSERVER
	? process.env.NEXT_SERVER_BACKEND_URL
	: process.env.NEXT_PUBLIC_BACKEND_URL;

/**
 * URL base para las APIs de UMA, configurada a través de una variable de entorno, sólo funciona client side, se utiliza para el vademecum
 */
const uma_apis_url = process.env.NEXT_ISSERVER
	? process.env.NEXT_SERVER_MEGALITH_URL
	: process.env.NEXT_PUBLIC_UMA_APIS_BASE_URL;

/**
 * URL for operation-related endpoints.
 */
export const operation_url = `${alertAIApiUrl}/operation`

/**
 * 
 * @param operationId 
 * @returns Para ver los tests
 */
export const test_by_id = (operationId: string) =>
	`${alertAIApiUrl}/test/byOperation/${operationId}`;


/**
 * URL for exporting operations.
 */
export const operation_export_url = `${operation_url}/export`;

/**
 * URL for bulk-creating operations from a CSV upload (best-effort).
 */
export const operation_bulk_url = `${operation_url}/bulk`;

/**
 * URL for closing operations.
 */
export const close_operation_url = `${operation_url}/close`;

/**
 * Descartar una evaluación que quedó a medias: sale del listado y deja de
 * bloquear al operador, sin borrar lo cargado. Sólo admin.
 */
export const descartar_evaluacion_url = (operationId: number) =>
	`${operation_url}/${operationId}/descartar`;

/**
 * Cambiar el resultado de una evaluación cerrada: de apto a no apto o al revés.
 * Sólo admin. No dispara el mail de baja; eso es aparte y a mano.
 */
export const cambiar_apto_url = (operationId: number) =>
	`${operation_url}/${operationId}/apto`;

/**
 * Anular un protocolo ya emitido: deja de valer sin borrar nada. Sólo admin,
 * con motivo obligatorio y no reversible. Ticket de soporte 111453.
 */
export const anular_protocolo_url = (operationId: number) =>
	`${operation_url}/${operationId}/anular`;

/** Corrige la base / CRPC de una atención ya registrada. Ticket 26452. */
export const cambiar_base_url = (operationId: number) =>
	`${operation_url}/${operationId}/base`;

/** El historial de correcciones de base de una atención, para auditoría. */
export const reasignaciones_de_base_url = (operationId: number) =>
	`${operation_url}/${operationId}/reasignaciones-de-base`;

/**
 * El detalle de las atenciones en CSV, por línea y rango de fechas.
 *
 * Sólo admin y supervisor médico. El backend acota el rango a 31 días y arma el
 * archivo por lotes; acá sólo se construye la URL.
 */
export const exportar_atenciones_url = `${operation_url}/exportar`;

/**
 * La auditoría: quién descartó una evaluación y quién reclasificó un resultado,
 * cuándo y por qué. Sólo admin y supervisor médico.
 */
export const auditoria_url = `${alertAIApiUrl}/auditoria`;

/** Las bajas recientes, para la pantalla de notificaciones de baja. */
export const bajas_url = `${operation_url}/bajas`;

/** Reenvío del mail de baja de una evaluación ya cerrada como NO APTO. */
export const resend_baja_notification_url = (operationId: number) =>
	`${operation_url}/${operationId}/resend-baja-notification`;

/**
 * URL for checking the status of operations in progress.
 */
export const check_operation_url = `${operation_url}/inprogress`;

/**
 * URL for user-related endpoints.
 */
export const user_url = `${alertAIApiUrl}/user`;

/** Destinatarios del mail automático de bajas, por línea ferroviaria. */
export const baja_recipients_url = `${alertAIApiUrl}/baja-recipients`;

/**
 * URL for fetching the list of drivers.
 */
export const driver_list_url = `${user_url}/driverList`;

/**
 * URL for fetching the list of breathalyzers.
 */
export const breathalyzer_url = `${alertAIApiUrl}/breathalyzer`;

/** Sorteo de PSA (ticket 26885): si una atención lleva PSA y en qué estado. */
export const psa_de_la_atencion_url = (operationId: string | number) =>
	`${alertAIApiUrl}/psa/atenciones/${operationId}`;

/** Seguimiento del sorteo de PSA para el Dashboard. Admin y supervisor médico. */
export const psa_seguimiento_url = `${alertAIApiUrl}/psa/seguimiento`;

/** Cupos diarios de PSA por base. Admin y supervisor médico. */
export const psa_cupos_url = `${alertAIApiUrl}/psa/cupos`;

/** Registrar que el PSA asignado no se pudo hacer, con motivo. */
export const psa_no_realizado_url = (operationId: string | number) =>
	`${alertAIApiUrl}/psa/atenciones/${operationId}/no-realizado`;

/** Volver a sortear el PSA de una atención. Sólo existe en dev, para pruebas. */
export const psa_volver_a_sortear_url = (operationId: string | number) =>
	`${alertAIApiUrl}/psa/atenciones/${operationId}/volver-a-sortear`;

/** Un etilómetro puntual, para corregirlo. Sólo admin. */
export const etilometro_url = (barcode: string) =>
	`${breathalyzer_url}/${encodeURIComponent(barcode)}`;

/** Sección SIGOF (sólo ADMIN): integración con SIGOF2 de Trenes. */
export const sigof_resumen_url = `${alertAIApiUrl}/sigof/resumen`;
/** Período en días de Argentina (`YYYY-MM-DD`, los dos incluidos) y base opcional. */
export type FiltroSigof = { desde: string; hasta: string; baseId?: number | null };
const filtroSigof = ({ desde, hasta, baseId }: FiltroSigof) =>
	`desde=${desde}&hasta=${hasta}${baseId ? `&baseId=${baseId}` : ""}`;
export const sigof_tablero_url = (filtro: FiltroSigof) =>
	`${alertAIApiUrl}/sigof/tablero?${filtroSigof(filtro)}`;
export const sigof_envios_url = (estados: string[], filtro?: FiltroSigof) =>
	`${alertAIApiUrl}/sigof/envios?estados=${encodeURIComponent(estados.join(","))}${
		filtro ? `&${filtroSigof(filtro)}` : ""
	}`;
export const sigof_envio_url = (operationId: number) =>
	`${alertAIApiUrl}/sigof/envios/${operationId}`;
export const sigof_reintentar_url = (operationId: number) =>
	`${alertAIApiUrl}/sigof/envios/${operationId}/reintentar`;


/**
 * URL for fetching the list of roles.
 */
export const role_list_url = `${user_url}/roleList`;

/**
 * URL for fetching the list of bases.
 */
export const base_list_url = `${user_url}/baseList`;


/**
 * URL for fetching self-assestment.
 */
export const self_assestment_url = `${alertAIApiUrl}/self-assestment`;

/**
 * URL for the vademecum API endpoint.
 * This endpoint is used to fetch information about medications and their details.
 */
export const vademecum = `${uma_apis_url}/ai/vademecum`;

/**
 * API route interna del portal que proxea a Synapse Terminologies (FHIR
 * ValueSet $expand sobre el CodeSystem de Alfabeta). Corre server-side en
 * el Cloud Run del portal, lo que permite whitelistear una IP fija (Cloud
 * NAT) en Apigee/Synapse. Reemplaza al endpoint `vademecum` legacy lento.
 */
export const medicine_search_url = "/api/medicine-search";

/**
 * URL for fetching questions.
 */
export const questions_url = `${alertAIApiUrl}/questions`
/**
 * URL for the physical exam endpoints.
 */
export const physical_exam_url = `${alertAIApiUrl}/physical-exam`;

/**
 * Base URL for AI models endpoints.
 */
const aiModelsUrl = `${alertAIApiUrl}/ai-models`;

export const cvFatigue = `${aiModelsUrl}/cv-fatigue`;

/**
 * URL for the CV Fatigue detector AI model.
 */
export const cvFatigueDetectorUrl = `${aiModelsUrl}/fatigue-detector`;

/**
 * Endpoint base URL for the company controller.
 */
export const endpoint_base_company = `${alertAIApiUrl}/company`;


/**
 * Endpoint base URL for the substance-exam controller.
 */
export const endpoint_base_substance_exam = `${alertAIApiUrl}/substance-exam`;

/**
 * 
 * @param operationId id de la operación
 * Realiza una solicitud para iniciar la revisión de una operación específica.
 */
export const request_review = (operationId: string) =>
	`${alertAIApiUrl}/operation/${operationId}/request-review`;


export const user_me = `${alertAIApiUrl}/user/me`;

/**
 * Trae todas las operaciones del usuario
 */
export const operationsByMe = `${operation_url}/by-me`;

/**
 * 
 * Base url de controller bases
 */
export const bases_endpoints_url = `${alertAIApiUrl}/bases`
	
export const lastOperation = (uid: string) =>
	`${alertAIApiUrl}/operation/last-operation/${uid}`;


/**
 * Endpoint to get a specific company based on his id
 */
export const get_company = (companyId: string) =>
	`${alertAIApiUrl}/company/${companyId}`;

export const supervision_metrics_url = `${alertAIApiUrl}/supervision/metrics`;

export const technical_incident_url = `${alertAIApiUrl}/technical-incident`;

export const doctor_shift_url = `${alertAIApiUrl}/doctor-shift`;

export const doctor_session_url = `${alertAIApiUrl}/doctor-session`;

export const doctor_monitoring_url = `${alertAIApiUrl}/supervision/doctor-monitoring`;
export const nursing_activity_url = `${alertAIApiUrl}/supervision/nursing-activity`;
/** Performance por enfermero (27070, fase 2b). Sólo administración. */
export const nursing_performance_url = `${alertAIApiUrl}/supervision/nursing-performance`;
export const indicadores_por_linea_url = `${alertAIApiUrl}/supervision/indicadores-por-linea`;
export const lineas_de_indicadores_url = `${alertAIApiUrl}/supervision/indicadores-por-linea/lineas`;
export const controls_report_url = `${alertAIApiUrl}/supervision/controls-report`;

/** Protocolos hechos en papel: carga, búsqueda y URL firmada del archivo. */
export const protocolos_en_papel_url = `${alertAIApiUrl}/protocolos-en-papel`;

/**
 * URL para la caratula diaria por CRPC (vista de administrador).
 */
export const caratula_url = `${alertAIApiUrl}/supervision/caratula`;

/**
 * URL para la caratula mensual por LÍNEA (matriz de controles por categoría).
 */
export const caratula_mensual_url = `${alertAIApiUrl}/supervision/caratula-mensual`;
export const employee_search_url = `${alertAIApiUrl}/supervision/employee-search`;
export const employee_history_url = `${alertAIApiUrl}/supervision/employee-history`;
/**
 * El archivo de un control en papel, desde el historial por empleado. Es otro
 * endpoint que el de la pantalla de Protocolos en papel porque lo abren los
 * perfiles de Monitoreo, no sólo administración.
 */
export const archivo_de_control_manual_url = (id: number) =>
	`${alertAIApiUrl}/supervision/protocolos-en-papel/${id}/archivo`;

/** Quién está disponible ahora. Es lo único del monitoreo que ve el cliente. */
export const disponibilidad_url = `${alertAIApiUrl}/supervision/disponibilidad`;

/** Historial de jornadas de médicos y enfermeros. Sólo administración. */
export const historial_sesiones_url = `${alertAIApiUrl}/supervision/historial-sesiones`;

/** Sesiones que quedaron abiertas sin cerrar. Sólo administración. */
export const sesiones_abandonadas_url = `${alertAIApiUrl}/supervision/sesiones-abandonadas`;

/**
 * Una sesión puntual: `${sesiones_url}/:id/cerrar?tipo=medico|enfermero`.
 * Sólo administración.
 */
export const sesiones_url = `${alertAIApiUrl}/supervision/sesiones`;

export const collaborator_session_login_url = `${alertAIApiUrl}/collaborator-session/login`;
export const collaborator_session_logout_url = `${alertAIApiUrl}/collaborator-session/logout`;
export const collaborator_session_pause_url = `${alertAIApiUrl}/collaborator-session/pause`;
export const collaborator_session_unpause_url = `${alertAIApiUrl}/collaborator-session/unpause`;
export const collaborator_session_status_url = `${alertAIApiUrl}/collaborator-session/status`;
export const collaborator_session_base_url = `${alertAIApiUrl}/collaborator-session/base`;

export const base_excluded_url = (baseId: number) =>
	`${alertAIApiUrl}/bases/${baseId}/excluded-from-reports`;
